/**
 * On-demand loading for the researched scouting notes (NOTES.md §23, §29).
 *
 * The notes are by far the largest thing in the bundle — ~157kb of prose for
 * the top few hundred players, against a 400kb budget for the whole app — and
 * they are needed at exactly one moment: when someone opens a player's detail
 * panel. They are not needed to render the board, which is what NFR-8's
 * "working board in under 60 seconds" is actually measuring.
 *
 * So they are behind a dynamic `import()`, which Vite splits into its own
 * chunk. The board's first paint no longer waits on them, and the fetch happens
 * on the first panel open — by which point the visitor has a working board in
 * front of them and a few hundred milliseconds are invisible.
 *
 * The module promise is memoised rather than the notes themselves: the browser
 * caches the module either way, but memoising the promise means N rapid panel
 * opens produce one in-flight request rather than N.
 */

import { el } from './dom.ts';
import type { PlayerId } from '../core/types.ts';

type NotesModule = typeof import('../core/data/scoutingNotes.ts');

let notesModule: Promise<NotesModule> | null = null;

/** Load (once) the scouting-note data chunk. */
export function loadScoutingNotes(): Promise<NotesModule> {
  notesModule ??= import('../core/data/scoutingNotes.ts');
  return notesModule;
}

/**
 * Fill `host` with a player's scouting note, if he has one.
 *
 * `host` is created and positioned by the caller *synchronously*, so the note
 * lands in the right place in the panel rather than being appended to the
 * bottom whenever the chunk happens to arrive. An empty placeholder is
 * invisible, so there is nothing to see until there is something to show.
 *
 * Opening a second player before the first resolves is the case worth getting
 * right: the panel re-renders, which builds a fresh host and discards this one.
 * Writing into a detached node would be harmless but pointless, and writing
 * into a *reused* one would show the wrong player's note — so the write is
 * skipped unless this exact host is still in the document.
 */
export function renderScoutingNote(host: HTMLElement, playerId: PlayerId): void {
  void loadScoutingNotes()
    .then(({ getScoutingNote }) => {
      if (!host.isConnected) return;
      const note = getScoutingNote(playerId);
      if (!note) return;

      const days = Math.floor((Date.now() - new Date(note.last_updated).getTime()) / 86_400_000);
      host.appendChild(
        el(
          'h3',
          {},
          'Scouting note',
          el(
            'span',
            { class: `badge note-date${days > 60 ? ' stale' : ''}` },
            days > 60 ? `researched ${days} days ago` : `as of ${note.last_updated}`,
          ),
        ),
      );

      const summaryList = el('div', { class: 'blurb' });
      for (const sentence of note.summary) summaryList.appendChild(el('p', {}, sentence));
      host.appendChild(summaryList);

      if (note.watch.length > 0) {
        host.appendChild(el('h4', {}, 'Worth watching'));
        const watchList = el('ul', { class: 'risks' });
        for (const sentence of note.watch) watchList.appendChild(el('li', {}, sentence));
        host.appendChild(watchList);
      }
    })
    .catch(() => {
      // A failed chunk fetch costs the visitor one optional section of an
      // otherwise complete panel. Everything that decides a pick — value,
      // ranks, ADP, the live case — is already on screen and came from the
      // main bundle, so this stays silent rather than showing an error for
      // something nobody asked for.
    });
}
